import { getInsuranceCountryNameByTwoLetterCode } from '@luxuryescapes/lib-regions'
import { RESERVATION_TYPE_BOOK_LATER } from 'constants/reservation'

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function isPastEsimTripEnd(
  endDate: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!endDate) return false
  return new Date(endDate).getTime() < now.getTime()
}

export function getEsimActivationDate(
  departureDate: Date | string | null | undefined,
  endDate?: Date | string | null,
  now: Date = new Date(),
): Date | undefined {
  if (!departureDate) return undefined
  if (isPastEsimTripEnd(endDate, now)) return undefined
  if (isWithinEsimRedemptionWindow(departureDate, endDate ?? undefined, now)) return undefined
  return new Date(new Date(departureDate).getTime() - SEVEN_DAYS_MS)
}

export function isWithinEsimRedemptionWindow(
  departureDate: Date | string | undefined,
  endDate?: Date | string,
  now: Date = new Date(),
): boolean {
  if (!departureDate) return false
  const departure = new Date(departureDate).getTime()
  if (departure - now.getTime() > SEVEN_DAYS_MS) return false
  if (!endDate) return true
  return new Date(endDate).getTime() >= now.getTime()
}

/** BNBL hotel orders without travel dates cannot redeem eSIM yet — treat like no eligible promo code. */
export function isBnblOrderWithoutEsimDates(order: App.Order | undefined): boolean {
  if (!order) return false
  const isBnblOrder = order.items.some(item => item.reservationType === RESERVATION_TYPE_BOOK_LATER)
  if (!isBnblOrder) return false
  const esimBenefit = order.loyaltyDetails.esimBenefit
  return !esimBenefit?.departureDate && !esimBenefit?.endDate
}

export function getEsimGbForTier(
  loyaltyConfig: App.LoyaltyConfig | undefined,
  tier: App.LoyaltyTier | undefined,
): number | undefined {
  if (!loyaltyConfig || !tier) return undefined
  const esimBenefit = loyaltyConfig.regionTierBenefits[tier]?.find(b => b.code === 'esim_package')
  return esimBenefit?.value?.unit === 'GB' ? esimBenefit.value.amount : undefined
}

const countryCodeFallbackNames: Record<string, string> = {
  CN: 'China',
  TW: 'Taiwan',
  BN: 'Brunei',
  KR: 'South Korea',
  VA: 'Vatican City',
}

export function formatEsimDate(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatEsimDestination(
  countryCodes: Array<string>,
  locale: string,
  internationalTripLabel: string,
): string {
  const countryNames = countryCodes
    .map(countryCode => getInsuranceCountryNameByTwoLetterCode(countryCode) ?? countryCodeFallbackNames[countryCode.toUpperCase()])
    .filter((countryName): countryName is string => !!countryName)

  if (!countryNames.length) return internationalTripLabel
  if (countryNames.length === 1) return countryNames[0]

  if (typeof Intl.ListFormat === 'function') {
    return new Intl.ListFormat(locale, {
      style: 'long',
      type: 'conjunction',
    }).format(countryNames)
  }

  return `${countryNames.slice(0, -1).join(', ')} & ${countryNames[countryNames.length - 1]}`
}

export function formatEsimDateRange(
  departureDate: string | null,
  endDate: string | null,
  locale: string,
): string {
  const departure = departureDate ? new Date(departureDate) : undefined
  const end = endDate ? new Date(endDate) : undefined
  if (!departure) return ''
  if (!end) return formatEsimDate(departure, locale)
  return `${departure.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${formatEsimDate(end, locale)}`
}
